import { useState, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { Grid, MenuItem, Button, Stack, FormHelperText } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import type { ModifyPerimetreModelForm, Perimetre, Territoire } from "models";
import { territoiresService, perimetresService, networkService } from "services";
import { DATE_FORMAT_FOR_API_REQUEST, patchUtils, perimetresUtil } from "utils";
import { SdeappsError, useErrorHandler, useSnackbarErrorHandler } from "utils/errorHandling";
import LoadingScreen from "components/Template/LoadingScreen";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { dateUtil } from "@sdeapps/react-core";
import ControlledTextField from "components/inputs/ControlledTextField";
import ControlledDateTime from "components/inputs/ControlledDateTime";
import ToastMessages from "constants/ToastMessages";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { useSearch } from "providers";
import { routesConfig } from "config/app-config";
import { validDateRules } from "constants/InputRules";
import type { Operation } from "fast-json-patch";

const DATE_FORMAT = "dd/MM/yyyy";

function getFormattedDateStringOrNull(date?: string | Date | null): string | null {
  if (date != null) {
    return dateUtil.format(date, DATE_FORMAT);
  }

  return null;
}

async function isModifiedPerimetreReplicated(
  modifiedPerimetre: ModifyPerimetreModelForm
): Promise<boolean> {
  const distantPerimetre = await perimetresService.getById(modifiedPerimetre.id);

  return (
    dateUtil.format(modifiedPerimetre.dateMembre, DATE_FORMAT) ===
      dateUtil.format(distantPerimetre.dateMembre, DATE_FORMAT) &&
    dateUtil.format(modifiedPerimetre.dateTransfertComplet, DATE_FORMAT) ===
      dateUtil.format(distantPerimetre.dateTransfertComplet, DATE_FORMAT) &&
    modifiedPerimetre.libelle === distantPerimetre.libelle &&
    modifiedPerimetre.idTerritoire === distantPerimetre.territoire.id &&
    modifiedPerimetre.centreFinancier === distantPerimetre.centreFinancier
  );
}

function toModifyPerimetreModelForm(perimetre: Perimetre): ModifyPerimetreModelForm {
  return {
    id: perimetre.id,
    idTerritoire: perimetre.territoire.id,
    libelle: perimetre.libelle,
    centreFinancier: perimetre.centreFinancier ?? "",
    dateMembre: perimetre.dateMembre != null ? dateUtil.getDate(perimetre.dateMembre) : null,
    dateTransfertComplet:
      perimetre.dateTransfertComplet != null
        ? dateUtil.getDate(perimetre.dateTransfertComplet)
        : null,
  };
}

function getOnlyModifiedPropsPatchData(
  perimetre: Partial<ModifyPerimetreModelForm>,
  perimetreInitial: Perimetre
): Array<Operation> {
  const toReplaceProps: Array<[string, unknown]> = [];
  const toRemoveProps: Array<string> = [];

  if (perimetre.libelle !== perimetreInitial.libelle) {
    toReplaceProps.push(["/libelle", perimetre.libelle]);
  }

  if (perimetre.centreFinancier !== perimetreInitial.centreFinancier) {
    toReplaceProps.push(["/centreFinancier", perimetre.centreFinancier]);
  }

  if (
    dateUtil.isValid(perimetre.dateMembre) &&
    getFormattedDateStringOrNull(perimetre.dateMembre) !==
      getFormattedDateStringOrNull(perimetreInitial.dateMembre)
  ) {
    if (perimetre.dateMembre == null) {
      toRemoveProps.push("/dateMembre");
    } else {
      toReplaceProps.push([
        "/dateMembre",
        dateUtil.format(perimetre.dateMembre, DATE_FORMAT_FOR_API_REQUEST),
      ]);
    }
  }

  if (
    dateUtil.isValid(perimetre.dateTransfertComplet) &&
    getFormattedDateStringOrNull(perimetre.dateTransfertComplet) !==
      getFormattedDateStringOrNull(perimetreInitial.dateTransfertComplet)
  ) {
    if (perimetre.dateTransfertComplet == null) {
      toRemoveProps.push("/dateTransfertComplet");
    } else {
      toReplaceProps.push([
        "/dateTransfertComplet",
        dateUtil.format(perimetre.dateTransfertComplet, DATE_FORMAT_FOR_API_REQUEST),
      ]);
    }
  }

  if (perimetre.idTerritoire !== perimetreInitial.territoire.id) {
    toReplaceProps.push(["/idTerritoire", perimetre.idTerritoire]);
  }

  const patchData: Array<Operation> = [];
  patchUtils.addReplaceToPatchData(toReplaceProps, patchData);
  patchUtils.addRemoveToPatchData(toRemoveProps, patchData);

  return patchData;
}

interface PerimetreFormProps {
  perimetre: Perimetre;
}

function PerimetreForm({ perimetre: perimetreInitial }: Readonly<PerimetreFormProps>): ReactNode {
  const [territoires, setTerritoires] = useState<Array<Territoire>>([]);
  const [modifiedPatchData, setModifiedPatchData] = useState<Array<Operation>>([]);
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const navigate = useNavigate();
  const { update } = useSearch();

  if (perimetresUtil.isPerimetreSansTerritoire(perimetreInitial)) {
    throw new Error(
      "Désolé, la modification de Périmètre n'est pour l'instant pas disponible pour un périmètre sans Territoire."
    );
  }

  const { catchErrors, isLoading } = useErrorHandler();
  // useSnackbarErrorHandler permet de préciser à l'utilisateur la faute dans la requête qui n'aurait pas permis de modifier le périmètre
  const { catchErrors: catchCreateErrors, isLoading: isSending } = useSnackbarErrorHandler();

  const formMethods = useForm<ModifyPerimetreModelForm>({
    shouldFocusError: false,
    defaultValues: toModifyPerimetreModelForm(perimetreInitial),
  });
  const { handleSubmit, control } = formMethods;
  const perimetre = useWatch({ control });

  useEffect(() => {
    async function getTerritoires(): Promise<void> {
      const _territoires = await territoiresService.getAll();
      setTerritoires(_territoires);
    }

    void catchErrors(getTerritoires);
    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getAll();
      setPerimetres(_perimetres);
    }

    void catchErrors(getPerimetres);
  }, [catchErrors]);

  useEffect(() => {
    setModifiedPatchData(getOnlyModifiedPropsPatchData(perimetre, perimetreInitial));
  }, [perimetre, perimetreInitial]);

  const submitPerimetre: SubmitHandler<ModifyPerimetreModelForm> = async function (
    data: ModifyPerimetreModelForm
  ): Promise<void> {
    async function modifyPerimetre(): Promise<void> {
      await perimetresService.modify(
        modifiedPatchData,
        perimetreInitial.territoire.id,
        perimetreInitial.id
      );

      /**
       * On attend que la réplication des données soit terminée pour mettre à jour
       * toutes les listes de la recherche.
       */
      await networkService.waitForReplication(
        async () => {
          if (!(await isModifiedPerimetreReplicated(data))) {
            throw new SdeappsError("Modification pas encore répliquée");
          }
        },
        20,
        () => {
          update();
          enqueueSnackbar({
            variant: "success",
            message: ToastMessages.MODIFIED_PERIMETRE,
          });
          navigate(routesConfig.perimetre.getParameterPath(data.id));
        },
        () => {
          enqueueSnackbar({
            variant: "warning",
            message: ToastMessages.MODIFIED_PERIMETRE_LONG_REPLICATION,
          });
          update();
        }
      );
    }

    await catchCreateErrors(modifyPerimetre);
  };

  const perimetresUsingSameCentre = useMemo(() => {
    if (perimetre.centreFinancier == null || perimetre.centreFinancier === "") {
      return [] as Array<Perimetre>;
    }
    return perimetres
      .filter((p) => p.id !== perimetreInitial.id)
      .filter((p) => p.centreFinancier === perimetre.centreFinancier);
  }, [perimetre.centreFinancier, perimetreInitial.id, perimetres]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <Grid component="form" onSubmit={handleSubmit(submitPerimetre)} container spacing={4}>
      <FormProvider {...formMethods}>
        <Grid container spacing={4} size={12}>
          <Grid size={12}>
            <ControlledTextField
              name="libelle"
              defaultValue=""
              rules={{ required: "Saisir un nom est obligatoire" }}
              fullWidth
              label="Désignation du périmètre *"
              variant="outlined"
              placeholder="Commission locale de ..."
            />
          </Grid>
          <Grid container size={{ xs: 12, sm: 6 }}>
            <ControlledDateTime name="dateMembre" rules={validDateRules} label="Date de création" />
          </Grid>
          <Grid container size={{ xs: 12, sm: 6 }}>
            <ControlledDateTime
              name="dateTransfertComplet"
              rules={validDateRules}
              label="Date de transfert complet"
            />
          </Grid>
          <Grid size={12}>
            <ControlledTextField
              select
              name="idTerritoire"
              rules={{ required: "Choisir un territoire est obligatoire" }}
              label="Territoire de rattachement *">
              {territoires.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.libelle}
                </MenuItem>
              ))}
            </ControlledTextField>
          </Grid>
          <Grid size={12}>
            <ControlledTextField
              name="centreFinancier"
              defaultValue=""
              label="Centre financier"
              rules={{
                validate: {
                  noReset: (value: string | undefined) =>
                    perimetreInitial.centreFinancier == null ||
                    perimetreInitial.centreFinancier.trim() === "" ||
                    (value != null && value.trim() !== "") ||
                    "Le code de centre financier ne peut pas être réinitialisé, renseignez un autre code ou gardez l'ancien.",
                  numberExpected: (value: string | undefined) =>
                    value == null ||
                    value.trim() === "" ||
                    !Number.isNaN(Number(value)) ||
                    "Le code de centre financier doit être un nombre.",
                },
              }}
            />
            {perimetresUsingSameCentre.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 1 }}>
                <InfoIcon color="info" />
                <FormHelperText>
                  Ce centre financier est déjà utilisé par {perimetresUsingSameCentre.length}{" "}
                  périmètre
                  {perimetresUsingSameCentre.length > 1 ? "s" : ""}:{" "}
                  {perimetresUsingSameCentre.map((p) => p.libelle).join(", ")}
                </FormHelperText>
              </Stack>
            )}
          </Grid>
        </Grid>
        <Grid container size={12} sx={{ gap: 2 }}>
          <Button
            onClick={() => {
              navigate(-1);
            }}
            color="error">
            Annuler
          </Button>
          <Button
            variant="contained"
            loading={isSending}
            disabled={modifiedPatchData.length === 0}
            loadingPosition="start"
            type="submit">
            Enregistrer
          </Button>
        </Grid>
      </FormProvider>
    </Grid>
  );
}

export default PerimetreForm;
